import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import FeedbackIcon from '@mui/icons-material/Feedback';
import RateReviewIcon from '@mui/icons-material/RateReview';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';

// Public pages - visible to everyone (not logged in)
const publicNavPages = [
    { label: 'Menu', path: '/menu', icon: <RestaurantMenuIcon fontSize="small" /> },
];

// Private pages - visible only to logged in users (non-admin)
const authNavPages = [
    { label: 'Menu', path: '/menu', icon: <RestaurantMenuIcon fontSize="small" /> },
    { label: 'Recepty', path: '/foods', icon: <MenuBookIcon fontSize="small" /> },
    { label: 'Zpětná vazba', path: '/feedback', icon: <FeedbackIcon fontSize="small" /> },
];

// Admin submenu pages
const adminPages = [
    { label: 'Uživatelé', path: '/admin/users', icon: <PeopleIcon fontSize="small" /> },
    { label: 'Ingredience', path: '/admin/ingredients', icon: <InventoryIcon fontSize="small" /> },
    { label: 'Zpětná vazba', path: '/admin/feedback', icon: <RateReviewIcon fontSize="small" /> },
];

function Layout() {
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [anchorElAdmin, setAnchorElAdmin] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const { isAuthenticated, isAdmin, logout } = useAuth();
    const { mode, toggleTheme } = useTheme();

    const getThemeIcon = () => {
        switch (mode) {
            case 'light':
                return <LightModeIcon />;
            case 'dark':
                return <DarkModeIcon />;
            case 'system':
            default:
                return <SettingsBrightnessIcon />;
        }
    };

    const getThemeTooltip = () => {
        switch (mode) {
            case 'light':
                return 'Světlý režim (klikněte pro tmavý)';
            case 'dark':
                return 'Tmavý režim (klikněte pro systémový)';
            case 'system':
            default:
                return 'Systémový režim (klikněte pro světlý)';
        }
    };

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleOpenAdminMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElAdmin(event.currentTarget);
    };

    const handleCloseAdminMenu = () => {
        setAnchorElAdmin(null);
    };

    const handleLogout = () => {
        logout();
        handleCloseUserMenu();
        navigate('/login');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppBar 
                position="sticky" 
                elevation={0}
                sx={{ 
                    backdropFilter: 'blur(8px)',
                    backgroundColor: (theme) => 
                        theme.palette.mode === 'light' 
                            ? 'rgba(230, 81, 0, 0.95)' 
                            : 'rgba(45, 45, 45, 0.95)',
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Desktop Logo */}
                        <RestaurantIcon 
                            sx={{ 
                                display: { xs: 'none', md: 'flex' }, 
                                mr: 1,
                                fontSize: 32,
                            }} 
                        />
                        <Typography
                            variant="h6"
                            noWrap
                            component={RouterLink}
                            to="/menu"
                            sx={{
                                mr: 4,
                                display: { xs: 'none', md: 'flex' },
                                fontWeight: 700,
                                color: 'inherit',
                                textDecoration: 'none',
                                '&:hover': {
                                    opacity: 0.9,
                                },
                            }}
                        >
                            Co budeme jíst?
                        </Typography>

                        {/* Mobile Menu Button */}
                        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                            <IconButton
                                size="large"
                                aria-label="menu"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleOpenNavMenu}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={handleCloseNavMenu}
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                }}
                            >
                                {(isAuthenticated ? authNavPages : publicNavPages).map((page) => (
                                    <MenuItem
                                        key={page.label}
                                        component={RouterLink}
                                        to={page.path}
                                        onClick={handleCloseNavMenu}
                                        sx={{ gap: 1 }}
                                    >
                                        {page.icon}
                                        <Typography>{page.label}</Typography>
                                    </MenuItem>
                                ))}
                                {isAdmin && (
                                    <>
                                        <Divider />
                                        <MenuItem disabled sx={{ opacity: 0.7 }}>
                                            <ListItemIcon>
                                                <AdminPanelSettingsIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>Admin</ListItemText>
                                        </MenuItem>
                                        {adminPages.map((page) => (
                                            <MenuItem
                                                key={page.label}
                                                component={RouterLink}
                                                to={page.path}
                                                onClick={handleCloseNavMenu}
                                                sx={{ pl: 4, gap: 1 }}
                                            >
                                                {page.icon}
                                                <Typography>{page.label}</Typography>
                                            </MenuItem>
                                        ))}
                                    </>
                                )}
                            </Menu>
                        </Box>

                        {/* Mobile Logo */}
                        <RestaurantIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
                        <Typography
                            variant="h6"
                            noWrap
                            component={RouterLink}
                            to="/menu"
                            sx={{
                                mr: 2,
                                display: { xs: 'flex', md: 'none' },
                                flexGrow: 1,
                                fontWeight: 700,
                                color: 'inherit',
                                textDecoration: 'none',
                            }}
                        >
                            Co budeme jíst?
                        </Typography>

                        {/* Desktop Navigation */}
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                            {(isAuthenticated ? authNavPages : publicNavPages).map((page) => (
                                <Button
                                    key={page.label}
                                    component={RouterLink}
                                    to={page.path}
                                    onClick={handleCloseNavMenu}
                                    startIcon={page.icon}
                                    sx={{ 
                                        my: 2, 
                                        color: 'white', 
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                    }}
                                >
                                    {page.label}
                                </Button>
                            ))}
                            {isAdmin && (
                                <>
                                    <Button
                                        onClick={handleOpenAdminMenu}
                                        startIcon={<AdminPanelSettingsIcon />}
                                        endIcon={<ArrowDropDownIcon />}
                                        sx={{ 
                                            my: 2, 
                                            color: 'white', 
                                            px: 2,
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                        }}
                                    >
                                        Admin
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElAdmin}
                                        open={Boolean(anchorElAdmin)}
                                        onClose={handleCloseAdminMenu}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                    >
                                        {adminPages.map((page) => (
                                            <MenuItem
                                                key={page.label}
                                                component={RouterLink}
                                                to={page.path}
                                                onClick={handleCloseAdminMenu}
                                            >
                                                <ListItemIcon>{page.icon}</ListItemIcon>
                                                <ListItemText>{page.label}</ListItemText>
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            )}
                        </Box>

                        {/* Theme Toggle Button */}
                        <Tooltip title={getThemeTooltip()}>
                            <IconButton
                                onClick={toggleTheme}
                                color="inherit"
                                sx={{ mr: 1 }}
                            >
                                {getThemeIcon()}
                            </IconButton>
                        </Tooltip>

                        <Box sx={{ flexGrow: 0 }}>
                            {isAuthenticated ? (
                                <>
                                    <Tooltip title="Open settings">
                                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                            <Avatar alt="User" src="/static/images/avatar/2.jpg" />
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        sx={{ mt: '45px' }}
                                        id="menu-appbar"
                                        anchorEl={anchorElUser}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        keepMounted
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                    >
                                        <MenuItem onClick={handleCloseUserMenu}>
                                            <Typography textAlign="center">Profil</Typography>
                                        </MenuItem>
                                        <MenuItem 
                                            component={RouterLink} 
                                            to="/menu/configure" 
                                            onClick={handleCloseUserMenu}
                                        >
                                            <Typography textAlign="center">Konfigurace menu</Typography>
                                        </MenuItem>
                                        <MenuItem onClick={handleLogout}>
                                            <Typography textAlign="center">Odhlásit</Typography>
                                        </MenuItem>
                                    </Menu>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        component={RouterLink}
                                        to="/login"
                                        color="inherit"
                                        variant="outlined"
                                    >
                                        Přihlásit
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to="/register"
                                        color="inherit"
                                        variant="contained"
                                    >
                                        Registrovat
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
            
            {/* Main Content */}
            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1,
                    py: 3,
                }}
            >
                <Container maxWidth="lg">
                    <Outlet />
                </Container>
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    py: 3,
                    px: 2,
                    mt: 'auto',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                }}
            >
                <Container maxWidth="lg">
                    <Typography variant="body2" color="text.secondary" align="center">
                        Co budeme jíst? - Plánování jídel
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
}

export default Layout;